import { useEffect, useState } from 'react';

import { getZonePendingOverview } from './useIAM';

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls the zone-access-request queue for its total pending count (summed
 * across every zone), so admin nav entries can show a badge instead of
 * requiring someone to open Zone Approvals to notice new requests.
 *
 * Silently returns 0 on any fetch error (network hiccup, no permission,
 * endpoint unavailable) -- a missing badge is a minor UX gap, not
 * something that should ever surface as a visible error to the user.
 */
export function useZonePendingCount(enabled: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const fetchCount = async () => {
      try {
        const zones = await getZonePendingOverview();
        const total = zones.reduce((sum, z) => sum + z.pending.length, 0);
        if (!cancelled) {
          setCount(total);
        }
      } catch {
        if (!cancelled) {
          setCount(0);
        }
      }
    };

    fetchCount();
    intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enabled]);

  return count;
}
